import {
  compose,
  COMPOSER_DURATIONS,
  type ComposerFacts,
  type ComposerInput,
  type ComposerPolicy,
  NO_GRADE_REFUSAL,
  gridRateFor,
  oneDecimalLabel,
  TAEG_HARD_CEILING_PCT,
} from "@hakili/domain-types";
import { type OwnFile, ownFileSchema } from "@hakili/contracts";
import { formatMoney, formatQuantity, t } from "@hakili/i18n";
import {
  ActionBar,
  BANK_FS_MICRO,
  Button,
  Callout,
  Card,
  Input,
  NumberStepper,
  SegmentedControl,
  Textarea,
} from "@hakili/ui";
import { AlertTriangle, Info, Lock, Scale, Send } from "lucide-react";
import { type ReactElement, useEffect, useRef, useState } from "react";

import { type AnalysisOfferView } from "./analyse.js";
import { asSentence } from "../sentence.js";
import { CAUSE_LABELS } from "./briefing.js";
import { DASH, fill } from "./dossiers.js";
import { InertAct } from "./inert-act.js";

/**
 * Round `L-1`, `M3` , **the composer to the drawing** (`hakili-bank.jsx`,
 * `OfferComposer`; WF-BNK-09; `P1-BKW-009`, `P1-BKW-010`). The amount is the
 * FILE's, read-only; the duration 36 / 48 / 60 with 60 the ceiling (I1); the
 * nominal rate typed. The accessory block with its standing sentence and the
 * two toggles, each showing its amount; the warranty line in its two states.
 * The live recap , total financed, the monthly with insurance, the capacity
 * bar and its percentage, the two fee lines apart, the TAEG chip and the
 * formula in plain text , derived by the SAME function the door judges the
 * save and the send by (`@hakili/domain-types`' `compose`, WF-BNK-09's
 * acceptance: the check runs live while typing). The rose block « règle
 * éliminatoire » and the amber « dérogation requise » live; the send disabled
 * while either stands or a justification is empty. The delegation threshold
 * read-only with its link to Politique, greyed with its round. Hakili's
 * proposal and the comparables: dashes (R81).
 *
 * `A-2` (D-1021-1, D3 §2), **the look rebuilt AROUND `OFFER-GUARDS`' logic, which is kept as it stands: its words,
 * its blocks and gaps, its `data-testid`** (`bank-composer-sans-note` among them). The fields are the kit's `Input`
 * and `Textarea`, the blocks, gaps and the two guards' sentences are its `Callout`, the send stands in its
 * `ActionBar`; the two fee choices are a state and not a call to act, so they are drawn in ink, never in the
 * send button's colour (one primary, audit §5); the TAEG's formula stands behind « Comment le TAEG est calculé »
 * (R-12).
 */
export interface ComposerFactsView {
  readonly amountFinanced: number;
  readonly depositPct: number | null;
  readonly durationMonths: number | null;
  readonly feeFinancingRequested: boolean | null;
  readonly hakiliFeeAmount: number;
  readonly bankFeeAmount: number;
  readonly capacityAmount: number | null;
  readonly capacityAtSubmissionAmount: number;
  readonly netSalaryAmount: number;
  readonly existingCommitmentsAmount: number;
  readonly expertisedValueAmount: number | null;
  readonly vehicleYear: number | null;
  readonly ltvMaxPct: number | null;
  readonly borrowerInsuranceRatePct: number;
  readonly policy: ComposerPolicy | null;
  readonly grade: string | null;
  /** Position 59: the submission's own run has not ended; the composer prints the note, never « aucun écart ». */
  readonly gradePending?: boolean;
  readonly year: number;
}

export interface StoredOfferView {
  readonly durationMonths: number;
  readonly nominalRatePct: string;
  readonly financesBankFee: boolean;
  readonly financesHakiliFee: boolean;
  readonly justification: string;
  readonly submittedAt: string | null;
  /** R-I (`L-3`, `M0 (d)`): the bank's own agency text; `null` until the analyst enters it. */
  readonly branchLabel: string | null;
}

const asNumber = (value: unknown): number | null => (typeof value === "number" ? value : null);
const asString = (value: unknown): string | null => (typeof value === "string" ? value : null);

/** The door's facts, parsed at the boundary; `null` for an answer this screen cannot render. */
export function composerFactsFrom(answer: unknown): ComposerFactsView | null {
  if (typeof answer !== "object" || answer === null) return null;
  const raw = (answer as Record<string, unknown>)["facts"];
  if (typeof raw !== "object" || raw === null) return null;
  const facts = raw as Record<string, unknown>;
  const amount = asNumber(facts["amountFinanced"]);
  const hakili = asNumber(facts["hakiliFeeAmount"]);
  const bank = asNumber(facts["bankFeeAmount"]);
  // `CAPACITY-ONE`: the run's capacity (`null` while none was computed) and the figure the file froze, told apart.
  const rawCapacity = facts["capacityAmount"];
  const capacity = asNumber(rawCapacity);
  const atSubmission = asNumber(facts["capacityAtSubmissionAmount"]);
  const insurance = asNumber(facts["borrowerInsuranceRatePct"]);
  const year = asNumber(facts["year"]);
  if (
    amount === null ||
    hakili === null ||
    bank === null ||
    (capacity === null && rawCapacity !== null) ||
    atSubmission === null ||
    insurance === null ||
    year === null
  ) {
    return null;
  }
  const policy = facts["policy"];
  return {
    amountFinanced: amount,
    depositPct: asNumber(facts["depositPct"]),
    durationMonths: asNumber(facts["durationMonths"]),
    feeFinancingRequested:
      typeof facts["feeFinancingRequested"] === "boolean" ? facts["feeFinancingRequested"] : null,
    hakiliFeeAmount: hakili,
    bankFeeAmount: bank,
    capacityAmount: capacity,
    capacityAtSubmissionAmount: atSubmission,
    netSalaryAmount: asNumber(facts["netSalaryAmount"]) ?? 0,
    existingCommitmentsAmount: asNumber(facts["existingCommitmentsAmount"]) ?? 0,
    expertisedValueAmount: asNumber(facts["expertisedValueAmount"]),
    vehicleYear: asNumber(facts["vehicleYear"]),
    ltvMaxPct: asNumber(facts["ltvMaxPct"]),
    borrowerInsuranceRatePct: insurance,
    policy: typeof policy === "object" && policy !== null ? (policy as ComposerPolicy) : null,
    grade: asString(facts["grade"]),
    gradePending: facts["gradePending"] === true,
    year,
  };
}

export function storedOfferFrom(answer: unknown): StoredOfferView | null {
  if (typeof answer !== "object" || answer === null) return null;
  const raw = (answer as Record<string, unknown>)["offer"];
  if (typeof raw !== "object" || raw === null) return null;
  const offer = raw as Record<string, unknown>;
  const duration = asNumber(offer["durationMonths"]);
  const rate = asString(offer["nominalRatePct"]);
  if (duration === null || rate === null) return null;
  return {
    durationMonths: duration,
    nominalRatePct: rate,
    financesBankFee: offer["financesBankFee"] === true,
    financesHakiliFee: offer["financesHakiliFee"] === true,
    justification: asString(offer["justification"]) ?? "",
    submittedAt: asString(offer["submittedAt"]),
    branchLabel: asString(offer["branchLabel"]),
  };
}

/** `CHEF-SELF-VALIDATION` 2a: the `ownFile` the composer's `GET` and `PUT …/offer` serve, read through the contract; `null` where the server serves none or a shape the contract does not name. */
export const ownFileFrom = (answer: unknown): OwnFile | null => {
  if (typeof answer !== "object" || answer === null) return null;
  const parsed = ownFileSchema.nullable().safeParse((answer as Record<string, unknown>)["ownFile"]);
  return parsed.success ? parsed.data : null;
};

export const reviewCompleteFrom = (answer: unknown): boolean =>
  typeof answer === "object" &&
  answer !== null &&
  (answer as Record<string, unknown>)["reviewComplete"] === true;

interface ComposerProps {
  readonly clientName: string;
  readonly reference: string;
  readonly facts: ComposerFactsView;
  readonly stored: StoredOfferView | null;
  readonly busy: boolean;
  /** `PUT /bank/files/{id}/offer` then `POST …/offer/submit` , « Envoyer au chef du crédit ». */
  /** R-I: the agency text rides beside the composer's input, never inside it (it composes nothing). */
  readonly onSend: (input: ComposerInput & { readonly branchLabel: string }) => void;
  /** `L-5`, `M1` (`P1-BKW-010`): « Voir la politique », the analyst's read-only copy; greyed when the screen is not given. */
  readonly onPolitique?: () => void;
  /**
   * `U-2`, `M3` (WF-BNK-09 step 1): the grid's proposal , the composer pre-filled with it while no
   * offer stands, editable; a rate under the grid opens the derogation as today. The avis phrased
   * through the port, editable, Hakili's kept beside hers. `null` with no run: the dash (R81).
   */
  readonly proposal?: AnalysisOfferView | null;
  /**
   * `ARC2` (D2 `usepropo`): the bay's « Reprendre la proposition ». The page counts the presses; each new count puts the term and the
   * rate back on Hakili's proposal , a change she made is replaced only by her own act on that button. `0`, the first draw, puts nothing.
   */
  readonly takeProposal?: number;
  /**
   * `CHEF-SELF-VALIDATION` 2a (`D-1029-2`): **what the SERVER says the chef who holds this file may do with it**, the route and the sentence it serves (`ownFile` of the
   * composer's `GET` and `PUT …/offer`); `null` for an analyste, and the composer is then what it always was. The browser compares no id and computes no route.
   * `validateAndSend` and `aboveDelegation` offer « Valider et envoyer au client » (the sentence, when served, as the info notice); `sendToCommittee` offers
   * « Envoyer au comité de crédit » under its served sentence; any other route withholds the send beside the served sentence, and « Réaffecter » (`onReassign`)
   * leads to where the file is given to an analyste.
   */
  readonly ownFile?: OwnFile | null;
  readonly onReassign?: () => void;
  readonly analysisSentence?: string | null;
  /** `OD-37` round 8: why Hakili proposes no offer (« note provisoire, 5 lectures manquantes »), shown where the proposal would be. */
  readonly offerReason?: string | null;
  /**
   * `CAPACITE-NON-CALCULEE` (D-1021-4): why the grade has no letter , the API's own cause and words (`incomplete` of the
   * grade) , printed beside the refusal as « Capacité non calculée : <words> », never composed here. `null` while the
   * grade is not known to be incomplete.
   */
  readonly gradeIncomplete?: { readonly cause: string; readonly words: string } | null;
  /**
   * `S3` (D2 `analyst.js:139`, `:161`; `app.js:203`, `ST(R()).view = null`): « Revenir à la revue » is a VIEW toggle, local to the screen: no door, no
   * write, the stage untouched. Absent, no control is drawn (D-1019-2).
   */
  readonly onRevenir?: () => void;
  /**
   * `#778` (D2 `bay.js:20-21`): the bay's face reads this composition, `compose(...).gaps.length || .blocks.length`: `true` with a block or a gap D2's `compose` lists (the
   * missing-row case is D2's separate box, `analyst.js:160`, and does not count), `false` with none, `null` while no composition stands. Reported on each change and as `null`
   * when the composer goes.
   */
  readonly onFlagging?: (flagging: boolean | null) => void;
  /** `C21` (D2 `g.prov`): the grade is provisional, so the conditions card opens on D2's callout; `false` or absent draws none. */
  readonly gradeProvisional?: boolean;
  /** `TERM-FIT` (`A-1c`): why the proposed term is not the term asked, in the server's words; `null` when it is the term asked. */
  readonly termNote?: string | null;
}

const pct = (value: string): string => value.replace(".", ",");

/** The ⚠ every warn callout of the composer carries (D2 `callout("warn", …)`): the above-card boxes, the in-card ones and the closed one, one constant (the designer's review 61b73e66, as `COMMITTEE_INFO_ICON`). **20 px, stroke 2** as D2's one constructor draws every callout's icon (`ui.js:8`, `ic(icon, 20, 2)`; the designer's re-look f259a5c0): the Politique's warn callouts take it too. */
export const COMPOSER_WARN_ICON = <AlertTriangle className="h-5 w-5" aria-hidden="true" />;
/** The scale of « Voir la politique de la banque » (D2 `analyst.js:139`, `:154`), in the recap card and in the closed callout alike. */
const POLICY_LINK_ICON = <Scale className="h-4 w-4" aria-hidden="true" />;
const GUARD_ID = "bank-composer-envoyer-raison";
/** The ⓘ of the info notice (D2 `callout("info", …)`): 20 px, indigo, as `COMMITTEE_INFO_ICON`. */
const OWN_FILE_INFO_ICON = <Info className="h-5 w-5 text-indigo-600" aria-hidden="true" />;
/** D2 `.btn.lg` (`components.css:84`): 48 px, 15 px, 600, the kit's `[data-size="lg"]`; the withheld send keeps the size. */
const SEND_CLASSES_INERT =
  "inline-flex min-h-[48px] cursor-not-allowed items-center gap-2 whitespace-nowrap rounded-[10px] border border-gray-300 bg-gray-100 px-[22px] text-[length:var(--hk-fs-read)] font-semibold text-gray-700";

/** D2 `.guard` (`components.css:88`): the reason a send waits, an amber chip carrying the lock. */
const GUARD_CHIP =
  "inline-flex max-w-full items-center gap-1.5 rounded-lg bg-[color:var(--hk-warn-bg)] px-3 py-1.5 text-[length:var(--hk-fs-body)] text-[color:var(--hk-warn)]";

/** A rate is a string `d.d` (never a float that prints as `10.700000000000001`); an integer takes its `.0`. */
const rateText = (value: number): string => (Number.isInteger(value) ? `${String(value)}.0` : String(value));

export function Composer(props: ComposerProps): ReactElement {
  const { facts, stored } = props;
  const asked = facts.feeFinancingRequested === true;
  const proposal = props.proposal ?? null;
  // `A-1c`: the duration starts on the stored offer's term, else Hakili's proposed term, else the term asked; with none of
  // them NOTHING is preselected (never 60 by default) and the send is guarded until she chooses.
  const [durationMonths, setDuration] = useState<number | null>(
    stored?.durationMonths ?? proposal?.durationMonths ?? facts.durationMonths ?? null,
  );
  const [rate, setRate] = useState<string>(stored?.nominalRatePct ?? proposal?.gridRatePct ?? "");
  const take = props.takeProposal ?? 0;
  const taken = useRef(take);
  useEffect(() => {
    if (take === taken.current) return;
    taken.current = take;
    if (proposal === null) return;
    setDuration(proposal.durationMonths);
    setRate(proposal.gridRatePct);
  }, [take, proposal]);
  const [financesBankFee, setBankFee] = useState<boolean>(stored?.financesBankFee ?? asked);
  const [financesHakiliFee, setHakiliFee] = useState<boolean>(stored?.financesHakiliFee ?? asked);
  const [justification, setJustification] = useState<string>(stored?.justification ?? "");
  // Ruling S2 (28 October): D2 draws no agency field. The offer door takes `branchLabel` as optional (`bank-offer.controller.ts:325`); a label already
  // stored on the offer is carried unchanged, never erased by this screen.
  const keptBranchLabel = stored?.branchLabel ?? "";

  const rateValid = /^\d{1,2}\.\d$/u.test(rate) && Number(rate) > 0;
  const input: ComposerInput | null =
    durationMonths === null
      ? null
      : {
          durationMonths,
          nominalRatePct: rateValid ? rate : "0.0",
          financesBankFee,
          financesHakiliFee,
          justification: justification.trim(),
        };
  const composerFacts: ComposerFacts = facts;
  // D-1021-4: no letter and no run pending , the offer is closed: nothing is computed or shown of it (no TAEG, no
  // mensualité, no usury check, no rate gap); the refusal and its cause stand beside the send.
  const closed = facts.grade === null && facts.gradePending !== true;
  const reason = props.offerReason ?? props.analysisSentence ?? null;
  const live = rateValid && !closed && input !== null ? compose(composerFacts, input) : null;
  const flagging =
    live === null ? null : live.blocks.length > 0 || live.gaps.some((gap) => gap.code !== "gridRateMissing");
  const { onFlagging } = props;
  useEffect(() => {
    onFlagging?.(flagging);
  }, [flagging, onFlagging]);
  useEffect(
    () => () => {
      onFlagging?.(null);
    },
    [onFlagging],
  );
  const ceiling = facts.policy === null ? null : facts.policy.taegCeilingPct;
  // The ceiling is min(policy, 14,0 %), and 14,0 % with no policy (`OFFER-GUARDS`): worded, never a dash.
  const regulatoryCeiling = pct(`${String(TAEG_HARD_CEILING_PCT)}.0`);
  // A letter with no row in the grid is a derogation before any rate is typed: the offer goes through the chef.
  const derogationKnown =
    facts.policy !== null &&
    facts.grade !== null &&
    !facts.policy.gradeRates.some((row) => row.grade === facts.grade);
  const feePct = (amount: number): string =>
    facts.amountFinanced > 0 ? String(Math.trunc((amount * 100) / facts.amountFinanced)) : DASH;
  const feePctLabel = (amount: number): string =>
    fill("bank.composer.fraisLigne", { montant: formatMoney(amount) });
  // The first press of the stepper, with no rate typed: the grade's own grid rate (Hakili's proposal reads it), else the grid's highest row; with
  // no grid at all the offer is closed and there is no stepper to press (D2 sets a constant 11,0 here: a constant is not a rule).
  const gridRates = facts.policy?.gradeRates ?? [];
  const highestRate = gridRates.reduce<string | null>(
    (best, row) => (best === null || Number(row.ratePct) > Number(best) ? row.ratePct : best),
    null,
  );
  const firstRate =
    proposal?.gridRatePct ?? gridRates.find((row) => row.grade === facts.grade)?.ratePct ?? highestRate;
  const ownFile = props.ownFile ?? null;
  const ownSends =
    ownFile !== null && (ownFile.route === "validateAndSend" || ownFile.route === "aboveDelegation");
  const ownCommittee = ownFile !== null && ownFile.route === "sendToCommittee";
  const ownWithheld = ownFile !== null && !ownSends && !ownCommittee;
  const sendable = live !== null && live.sendable && !props.busy && !ownWithheld;
  const sendLabel = ownSends
    ? t("bank.decision.validerEnvoyer")
    : ownCommittee
      ? t("bank.composer.envoyerComite")
      : live?.requiredLevel === "comiteCredit"
        ? t("bank.composer.envoyerComite")
        : live?.requiredLevel === "chefCredit" || (live === null && derogationKnown)
          ? t("bank.composer.envoyerChef")
          : t("bank.composer.envoyerValidation");
  // The sentences OFFER-GUARDS and the rules say, stood BESIDE the send (their `data-testid` kept): the control that is
  // withheld points at the one element that says why, so the reason is read once.
  const incomplete = props.gradeIncomplete ?? null;
  const causeLabel = incomplete === null ? null : (CAUSE_LABELS.get(incomplete.cause) ?? null);
  // `C22` (D2 `analyst.js:139` at `3c62617a`): the guard beside the send is D2's « Capacité non calculée : {cause}. », and the refusal itself
  // (`NO_GRADE_REFUSAL`) stays with the guarded send for the accessible name; with no cause given the refusal is what the guard says.
  const guardNode: ReactElement | null = closed ? (
    <span className={GUARD_CHIP} data-testid="bank-composer-sans-note">
      <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      {incomplete === null ? (
        NO_GRADE_REFUSAL
      ) : (
        <>
          <span className="sr-only">{NO_GRADE_REFUSAL} </span>
          {causeLabel === null
            ? `${t("bank.hakili.capaciteNonCalculee")}.`
            : fill("bank.briefing.capaciteNonCalculeeCause", { cause: causeLabel })}
        </>
      )}
    </span>
  ) : live === null ? null : live.blocks.length > 0 ? (
    <span className={GUARD_CHIP} data-testid="bank-composer-blocage-regle">
      <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
      {t("bank.composer.blocageRegle")}
    </span>
  ) : live.gradeMissing ? (
    <Callout tone="warn" testId="bank-composer-sans-note">
      {live.refusal}
    </Callout>
  ) : live.gradePending ? (
    <Callout tone="warn" testId="bank-composer-note-en-attente">
      {live.refusal}
    </Callout>
  ) : null;

  const reasonText =
    durationMonths === null
      ? t("bank.composer.choisirDuree")
      : live === null
        ? t("bank.composer.envoyerSaisirTaux")
        : (live.refusal ?? "");
  const sendIcon = <Send className="h-4 w-4" aria-hidden="true" />;
  const fieldLabel = "text-[length:var(--hk-fs-body)] font-semibold";
  const muted = "text-[color:var(--hk-text-2)]";
  const feeField = (
    id: "frais-banque" | "frais-hakili",
    label: string,
    amount: number,
    on: boolean,
    set: (next: boolean) => void,
    signatureLabel: string,
  ): ReactElement => (
    <div className="grid min-w-0 gap-1.5" data-testid={`bank-composer-${id}-champ`}>
      <span className={fieldLabel}>
        {label} <span className={`font-normal ${muted}`}>· {feePctLabel(amount)}</span>
      </span>
      <SegmentedControl
        label={label}
        tone="ink"
        value={on ? "financer" : "signature"}
        options={[
          { value: "financer", label: t("bank.composer.financer"), testId: `bank-composer-${id}-financer` },
          { value: "signature", label: signatureLabel, testId: `bank-composer-${id}-signature` },
        ]}
        onChange={(value) => {
          set(value === "financer");
        }}
        className="flex-nowrap justify-self-start [&>button]:px-[14px]"
      />
    </div>
  );
  const recapRow = (
    label: string,
    value: string,
    options: { readonly testId: string; readonly labelTestId?: string },
  ): ReactElement => (
    <div className="flex justify-between gap-4 border-b border-[color:var(--hk-line-soft)] py-[9px] last:border-0">
      <dt className={muted} data-testid={options.labelTestId}>
        {label}
      </dt>
      <dd className="text-right font-mono font-semibold" data-testid={options.testId}>
        {value}
      </dd>
    </div>
  );
  // The designer's verdict (D2 frame 191): the sentence served for a route that SENDS (`aboveDelegation`) is the info notice, icon and lavender band, above the action bar.
  const ownNotice =
    (ownSends || ownCommittee) && ownFile.sentence !== null ? (
      <Callout tone="info" testId="bank-composer-own-file-sentence" icon={OWN_FILE_INFO_ICON}>
        {ownFile.sentence}
      </Callout>
    ) : null;
  const sendBar = (
    <ActionBar label={t("bank.composer.barreActions")}>
      {sendable ? (
        <>
          <Button
            variant="primary"
            size="lg"
            onClick={() => {
              if (live.sendable && input !== null) props.onSend({ ...input, branchLabel: keptBranchLabel });
            }}
            className="inline-flex items-center gap-2"
            data-testid="bank-composer-envoyer"
          >
            {sendIcon}
            {sendLabel}
          </Button>
        </>
      ) : props.busy ? (
        // A transient state , the door is answering , and the only one the native `disabled` is for.
        <Button
          variant="primary"
          size="lg"
          disabled
          className="inline-flex items-center gap-2"
          data-testid="bank-composer-envoyer"
        >
          {sendIcon}
          {sendLabel}
        </Button>
      ) : (
        <>
          <InertAct describedBy={GUARD_ID} testId="bank-composer-envoyer" className={SEND_CLASSES_INERT}>
            {sendIcon}
            {sendLabel}
          </InertAct>
          <div id={GUARD_ID} className={`max-w-full min-w-0 flex-none${ownWithheld ? " basis-full" : ""}`}>
            {guardNode ??
              (ownWithheld && ownFile.sentence !== null ? (
                <span className={GUARD_CHIP} data-testid="bank-composer-envoyer-raison">
                  <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
                  {ownFile.sentence}
                </span>
              ) : reasonText === "" ? null : (
                <span className={GUARD_CHIP} data-testid="bank-composer-envoyer-raison">
                  <Lock className="h-[15px] w-[15px] shrink-0" aria-hidden="true" />
                  {reasonText}
                </span>
              ))}
          </div>
          {!ownWithheld || props.onReassign === undefined ? null : (
            <Button variant="ghost" onClick={props.onReassign} data-testid="bank-composer-reaffecter">
              {t("bank.equipe.reaffecter")}
            </Button>
          )}
        </>
      )}
      {props.onRevenir === undefined ? null : (
        <>
          <span className="flex-1" aria-hidden="true" />
          <Button variant="ghost" onClick={props.onRevenir} data-testid="bank-composer-revenir">
            {t("bank.composer.revenirRevue")}
          </Button>
        </>
      )}
    </ActionBar>
  );

  // `S2` (D2 frame 34): on an incomplete note the composer is the callout and the guarded send alone , no field, nothing to type.
  if (closed) {
    return (
      <section className="space-y-4" data-testid="bank-composer">
        <Callout
          tone="warn"
          testId="bank-composer-note-incomplete"
          className="w-full"
          icon={COMPOSER_WARN_ICON}
        >
          <p className="text-sm font-medium">{t("bank.composer.noteIncompleteTitre")}</p>
          {incomplete === null ? (
            reason === null ? null : (
              // `S1`: with no cause given, the one sentence the screen has for why there is no proposal stands here.
              <p className="mt-1 text-sm text-gray-700" data-testid="bank-composer-sans-proposition">
                {asSentence(reason)}
              </p>
            )
          ) : (
            // The cause's sentence exactly as the server serves it, no mark (D2's « texte provisoire » is a design pastille, never a product element).
            <p className="mt-1 text-sm text-gray-700" data-testid="bank-composer-capacite-non-calculee">
              {asSentence(incomplete.words)}
            </p>
          )}
          {/* `Q-LINK` (D2 frame 34, D2-design.md:72): « Voir la politique de la banque » belongs to the EMPTY-GRID cause alone, read from the
              cause's TYPED code and never its words; with no screen given there is no door, so it is not drawn (D-1019-2). */}
          {incomplete?.cause === "grille" && props.onPolitique !== undefined ? (
            <p className="mt-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={props.onPolitique}
                className="inline-flex items-center gap-1.5"
                data-testid="bank-composer-politique"
              >
                {POLICY_LINK_ICON}
                {t("bank.composer.politiqueLien")}
              </Button>
            </p>
          ) : null}
        </Callout>
        {sendBar}
      </section>
    );
  }

  const modifiedCallout =
    live?.modified === true ? (
      <Callout tone="info" testId="bank-composer-modifiee">
        <b>{t("bank.composer.offreModifiee")}</b>{" "}
        {fill("bank.composer.ecartDemandeSeul", {
          montant: formatMoney(Math.abs(live.capitalDeltaAmount)),
          signe: live.capitalDeltaAmount >= 0 ? "+" : "-",
        })}
      </Callout>
    ) : null;

  // `C19` (D2 `g.partial`): the file's letter is not in the grid, before and after any rate; the words are D2's, the lookup is the shared one.
  const partialLetter =
    facts.grade !== null && (facts.policy === null || gridRateFor(facts.policy, facts.grade) === null)
      ? facts.grade
      : null;
  const calloutLines = (title: string, lines: readonly string[]): ReactElement => (
    <>
      <p className="text-sm font-medium">{title}</p>
      {lines.map((line) => (
        <p key={line} className="mt-1 text-sm text-gray-700">
          {line}
        </p>
      ))}
    </>
  );
  // D2 `sig` (`analyst.js:161`), above the conditions card: the missing-row gap first (C20), then each rule block (its closing sentence inside
  // the callout, C18), then each gap (« dérogation requise »).
  const signalCallouts: ReactElement[] =
    live === null
      ? []
      : [
          ...live.gaps
            .filter((gap) => gap.code === "gridRateMissing")
            .map((gap) => (
              <Callout
                key={gap.code}
                tone="warn"
                testId={`bank-composer-ecart-${gap.code}`}
                icon={COMPOSER_WARN_ICON}
              >
                {calloutLines(fill("bank.composer.ecartPolitiqueTitre", { note: facts.grade ?? DASH }), [])}
                <p className="mt-1 text-sm text-gray-700">
                  {t("bank.composer.ecartPolitiqueAvant")} <b>{t("bank.composer.ecartPolitiqueNiveau")}</b>.{" "}
                  {t("bank.composer.ecartPolitiqueApres")}
                </p>
              </Callout>
            )),
          ...live.blocks.map((block) => (
            <Callout
              key={block.code}
              tone="bad"
              testId={`bank-composer-blocage-${block.code}`}
              icon={COMPOSER_WARN_ICON}
            >
              {calloutLines(fill("bank.composer.blocageTitre", { regle: block.rule }), [
                block.detail,
                t("bank.composer.blocageRegle"),
              ])}
            </Callout>
          )),
          ...live.gaps
            .filter((gap) => gap.code !== "gridRateMissing")
            .map((gap) => (
              <Callout
                key={gap.code}
                tone="warn"
                testId={`bank-composer-ecart-${gap.code}`}
                icon={COMPOSER_WARN_ICON}
              >
                {calloutLines(fill("bank.composer.derogationTitre", { regle: gap.rule }), [
                  gap.detail,
                  fill("bank.composer.validationNecessaire", {
                    level: t(
                      gap.level === "comiteCredit"
                        ? "bank.composer.niveauComite"
                        : "bank.composer.niveauChef",
                    ),
                  }),
                ])}
              </Callout>
            )),
        ];

  return (
    <section className="space-y-4" data-testid="bank-composer">
      {/* D2 `.compgrid` (`analyst.js:189`): the conditions and the sticky live recap, side by side from 1100 px, stacked below. */}
      <div
        className="grid grid-cols-[minmax(0,1fr)_360px] items-start gap-4 max-[1099px]:grid-cols-1"
        data-testid="bank-composer-grille"
      >
        <div className="grid min-w-0 gap-4">
          {signalCallouts}
          <div data-testid="bank-composer-conditions">
            <Card className="!p-0">
              <div className="px-5 pb-1 pt-4">
                <h2 className="text-[length:var(--hk-fs-lead)] font-semibold leading-snug">
                  {t("bank.composer.conditionsTitre")}
                </h2>
              </div>
              <div className="grid gap-[18px] px-5 pb-[18px] pt-3">
                {partialLetter === null ? null : (
                  <Callout tone="warn" testId="bank-composer-grille-partielle" icon={COMPOSER_WARN_ICON}>
                    <p className="text-sm">
                      <b>{fill("bank.composer.grillePartielleTitre", { note: partialLetter })}</b>{" "}
                      {t("bank.composer.grillePartielleCorps")}
                    </p>
                  </Callout>
                )}
                {props.gradeProvisional === true ? (
                  <Callout tone="warn" testId="bank-composer-note-provisoire" icon={COMPOSER_WARN_ICON}>
                    <p className="text-sm">
                      <b>{t("bank.composer.noteProvisoireTitre")}</b> {t("bank.composer.noteProvisoireCorps")}
                    </p>
                  </Callout>
                ) : null}
                <div className="grid grid-cols-2 gap-4 max-[1099px]:grid-cols-1">
                  <div className="grid gap-1.5">
                    <label htmlFor="bank-composer-montant" className={fieldLabel}>
                      {t("bank.composer.montant")}
                    </label>
                    <Input
                      id="bank-composer-montant"
                      readOnly
                      value={formatMoney(facts.amountFinanced)}
                      className="font-mono"
                      testId="bank-composer-montant"
                    />
                  </div>
                  <div className="grid gap-1.5">
                    <span className={fieldLabel}>{t("bank.composer.taux")}</span>
                    <NumberStepper
                      label={t("bank.composer.taux")}
                      decreaseLabel={t("bank.composer.tauxBaisser")}
                      increaseLabel={t("bank.composer.tauxMonter")}
                      value={rateValid ? Number(rate) : 0}
                      min={0.1}
                      max={99.9}
                      step={0.1}
                      format={(value) =>
                        rateValid
                          ? fill("bank.composer.tauxValeur", { n: pct(rateText(value)) })
                          : t("bank.feuille.vide")
                      }
                      onChange={(value) => {
                        if (rateValid) setRate(rateText(value));
                        else if (firstRate !== null) setRate(firstRate);
                      }}
                      className="justify-self-start"
                      testId="bank-composer-taux"
                    />
                  </div>
                </div>
                <div className="grid gap-1.5">
                  <span className={fieldLabel}>{t("bank.composer.duree")}</span>
                  <SegmentedControl
                    label={t("bank.composer.duree")}
                    value={durationMonths === null ? "" : String(durationMonths)}
                    options={COMPOSER_DURATIONS.map((months) => ({
                      value: String(months),
                      label: fill("bank.composer.mois", { n: String(months) }),
                      testId: `bank-composer-duree-${String(months)}`,
                    }))}
                    onChange={(value) => {
                      setDuration(Number(value));
                    }}
                    className="justify-self-start"
                  />
                  {proposal !== null && props.termNote !== undefined && props.termNote !== null ? (
                    <p
                      className={`text-[length:var(--hk-fs-body)] ${muted}`}
                      data-testid="bank-composer-duree-note"
                    >
                      {asSentence(props.termNote)}
                    </p>
                  ) : null}
                </div>
                {rateValid ? null : (
                  <p className={`text-[length:var(--hk-fs-body)] ${muted}`}>
                    {t("bank.composer.saisirTaux")}
                  </p>
                )}
                <div className="grid gap-2.5">
                  <p
                    className={`font-semibold uppercase tracking-[.12em] ${BANK_FS_MICRO} ${muted}`}
                    data-testid="bank-composer-accessoires-titre"
                  >
                    {t("bank.composer.accessoiresTitre")}
                  </p>
                  <p
                    className={`text-[length:var(--hk-fs-body)] ${muted}`}
                    data-testid="bank-composer-frais-aide"
                  >
                    {t("bank.composer.fraisAide")}
                    {facts.feeFinancingRequested === null
                      ? ""
                      : ` ${t(facts.feeFinancingRequested ? "bank.composer.fraisDemandeFinancer" : "bank.composer.fraisDemandeRegler")}`}
                  </p>
                  <div className="grid grid-cols-2 gap-4 max-[1099px]:grid-cols-1">
                    {feeField(
                      "frais-banque",
                      fill("bank.composer.fraisBanquePct", { taux: feePct(facts.bankFeeAmount) }),
                      facts.bankFeeAmount,
                      financesBankFee,
                      setBankFee,
                      t("bank.composer.aLaSignature"),
                    )}
                    {feeField(
                      "frais-hakili",
                      fill("bank.composer.fraisHakiliPct", { taux: feePct(facts.hakiliFeeAmount) }),
                      facts.hakiliFeeAmount,
                      financesHakiliFee,
                      setHakiliFee,
                      t("bank.composer.fraisAvantRdv"),
                    )}
                  </div>
                  {modifiedCallout}
                </div>
                {live !== null && live.gaps.length > 0 ? (
                  <div className="grid gap-1.5">
                    <label htmlFor="bank-composer-justification" className={fieldLabel}>
                      {live.gaps.every((gap) => gap.code === "gridRateMissing")
                        ? t("bank.composer.justificationLigne")
                        : t("bank.composer.justification")}{" "}
                      <span className={`font-normal ${muted}`}>· {t("bank.composer.obligatoire")}</span>
                    </label>
                    <Textarea
                      id="bank-composer-justification"
                      value={justification}
                      onChange={setJustification}
                      rows={3}
                      testId="bank-composer-justification"
                    />
                  </div>
                ) : null}
              </div>
            </Card>
          </div>
        </div>
        <div className="sticky top-4" data-testid="bank-composer-recap-carte">
          <Card className="!p-0">
            <div className="px-5 pb-1 pt-4">
              <h2 className="text-[length:var(--hk-fs-lead)] font-semibold leading-snug">
                {t("bank.composer.recapTitre")}
              </h2>
            </div>
            <div className="px-5 pb-[18px] pt-3 text-[length:var(--hk-fs-body)]">
              {live === null ? (
                <p className={muted} data-testid="bank-composer-recap-vide">
                  {durationMonths === null
                    ? t("bank.composer.choisirDureeRecap")
                    : t("bank.composer.recapAucun")}
                </p>
              ) : (
                <div data-testid="bank-composer-recap">
                  <dl>
                    {recapRow(t("bank.composer.totalFinance"), formatMoney(live.recap.totalFinancedAmount), {
                      testId: "bank-composer-total",
                    })}
                    {recapRow(t("bank.composer.mensualiteRecap"), formatMoney(live.recap.instalmentAmount), {
                      testId: "bank-composer-mensualite",
                    })}
                    {recapRow(
                      t("bank.composer.dontAssurance"),
                      formatMoney(live.recap.insuranceInstalmentAmount),
                      {
                        testId: "bank-composer-assurance",
                      },
                    )}
                    {recapRow(
                      t("bank.composer.capacite"),
                      live.recap.capacityAmount === null ? DASH : formatMoney(live.recap.capacityAmount),
                      { testId: "bank-composer-capacite" },
                    )}
                    {recapRow(
                      t("bank.composer.capaciteRestante"),
                      fill("bank.composer.capaciteUtilisee", {
                        pct: pct(String(live.recap.capacityUsedPct)),
                      }),
                      {
                        testId: "bank-composer-capacite-pct",
                        labelTestId: "bank-composer-capacite-etiquette",
                      },
                    )}
                    {recapRow(
                      t("bank.composer.fraisBanqueRecap"),
                      fill(
                        financesBankFee
                          ? "bank.composer.fraisValeurFinances"
                          : "bank.composer.fraisValeurSignature",
                        { montant: formatQuantity(live.recap.bankFeeAmount) },
                      ),
                      { testId: "bank-composer-frais-banque" },
                    )}
                    {recapRow(
                      t("bank.composer.fraisHakiliRecap"),
                      fill(
                        financesHakiliFee
                          ? "bank.composer.fraisValeurFinances"
                          : "bank.composer.fraisValeurAvantRdv",
                        { montant: formatQuantity(live.recap.hakiliFeeAmount) },
                      ),
                      { testId: "bank-composer-frais-hakili" },
                    )}
                    <div className="flex justify-between gap-4 py-[9px]">
                      <dt className={muted}>{t("bank.composer.taeg")}</dt>
                      <dd
                        className={
                          "max-w-full text-right font-mono font-semibold " +
                          (live.recap.underCeiling ? "" : "text-[color:var(--hk-bad)]")
                        }
                        data-testid="bank-composer-taeg"
                      >
                        {fill(
                          live.recap.underCeiling
                            ? ceiling === null
                              ? "bank.composer.taegSousReglementaire"
                              : "bank.composer.taegSous"
                            : "bank.composer.taegAuDessus",
                          {
                            taeg: pct(live.recap.taegPct),
                            plafond: ceiling === null ? regulatoryCeiling : pct(ceiling.replace(/\.0$/u, "")),
                          },
                        )}
                      </dd>
                    </div>
                  </dl>
                  <div
                    className="my-1.5 h-1.5 w-full overflow-hidden rounded bg-[color:var(--hk-line)]"
                    role="img"
                    aria-label={`${t("bank.composer.capaciteRestante")} ${pct(String(live.recap.capacityUsedPct))}\u202f%`}
                  >
                    <i
                      className="block h-full"
                      style={{
                        width: `${String(Math.min(100, live.recap.capacityUsedPct))}%`,
                        background: live.recap.capacityUsedPct > 100 ? "var(--hk-bad)" : "var(--hk-brand)",
                      }}
                    />
                  </div>
                  {live.recap.gridDeltaPts === null || live.recap.gridRatePct === null ? null : (
                    // D2 `analyst.js:163` (`.small`, margin 6px 0 10px): the shared composer's `gridDeltaPts`, never a subtraction here; amber only
                    // where the « Grille tarifaire » gap stands (`--hk-warn`), else `--hk-text-2`.
                    <p
                      className={`my-1.5 mb-2.5 text-[length:var(--hk-fs-body)] ${
                        live.gaps.some((gap) => gap.code === "rateBelowGrid")
                          ? "text-[color:var(--hk-warn)]"
                          : "text-[color:var(--hk-text-2)]"
                      }`}
                      data-testid="bank-composer-ecart-grille"
                    >
                      {fill(
                        live.recap.gridDeltaPts < 0
                          ? "bank.composer.grilleSous"
                          : live.recap.gridDeltaPts > 0
                            ? "bank.composer.grilleAu"
                            : "bank.composer.grilleSur",
                        {
                          points: pct(oneDecimalLabel(Math.abs(live.recap.gridDeltaPts))),
                          note: live.recap.gridDeltaGrade ?? "",
                          taux: pct(live.recap.gridRatePct),
                        },
                      )}
                    </p>
                  )}
                  {live.recap.capacityUsedPct > 100 ? (
                    <p
                      className="font-medium text-[color:var(--hk-bad)]"
                      data-testid="bank-composer-capacite-depassee"
                    >
                      {t("bank.composer.capaciteDepassee")}
                    </p>
                  ) : null}
                  {props.onPolitique === undefined ? null : (
                    <p className="my-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={props.onPolitique}
                        className="inline-flex items-center gap-1.5"
                        data-testid="bank-composer-politique"
                      >
                        {POLICY_LINK_ICON}
                        {t("bank.composer.politiqueLien")}
                      </Button>
                    </p>
                  )}
                  <details data-testid="bank-composer-formule-detail">
                    <summary className="cursor-pointer font-semibold text-[color:var(--hk-brand-strong)]">
                      {t("bank.composer.formuleTitre")}
                    </summary>
                    <p className={`mt-1.5 ${muted}`} data-testid="bank-composer-formule">
                      {fill("bank.composer.formule", {
                        taux: pct(rate),
                        frais: live.recap.feeImpactLabel,
                        duree: durationMonths === null ? DASH : String(durationMonths),
                      })}
                    </p>
                  </details>
                </div>
              )}
            </div>
          </Card>
        </div>
      </div>
      {ownNotice}
      {sendBar}
    </section>
  );
}
